import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { PushPipe } from '@ngrx/component';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxIconButtonModule } from '@wdx/shared/components/wdx-icon-button';
import {
    FilesFacadeService,
    FileUploadPackage,
    FileUploadStatus,
} from '@wdx/shared/infrastructure/file-io';
import {
    FileIndex,
    RandomStringPipe,
    TranslateTokenisedStringPipe,
    TRANSLATION_CANCEL_BTN,
    TRANSLATION_DRAG_AND_DROP_LABEL,
    TRANSLATION_FILE_UPLOAD_FAILED,
    TRANSLATION_OR_LABEL,
    TRANSLATION_RETRY_BTN,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { NgxDropzoneChangeEvent, NgxDropzoneModule } from 'ngx-dropzone';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'wdx-file-upload',
    templateUrl: './wdx-file-upload.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        NgbProgressbarModule,
        NgxDropzoneModule,
        PushPipe,
        WdxIconButtonModule,
        WdxIconModule,
        TranslateTokenisedStringPipe,
    ],
})
export class WdxFileUploadComponent extends WdxDestroyClass {
    @Input() disabled = false;
    @Input() imageUpload = false;
    @Input() customEndpoint!: string | undefined | null;
    @Input() customHeaders?: string[] | null;
    @Input() set reset(value: number | undefined) {
        if (value) {
            this.resetState();
        }
    }

    @Output() fileUploaded = new EventEmitter<FileIndex>();

    fileUploadStatus$?: Observable<FileUploadStatus>;
    fileUploadProgress$?: Observable<number>;
    fileUploadPackage$ = new BehaviorSubject<FileUploadPackage | null>(null);

    progressType$ = new BehaviorSubject<string | null>(null);
    rejectedFiles = false;

    readonly FILE_UPLOAD_STATUS = FileUploadStatus;
    readonly FILE_UPLOAD_FAILED = TRANSLATION_FILE_UPLOAD_FAILED;
    readonly DRAG_AND_DROP_LABEL = TRANSLATION_DRAG_AND_DROP_LABEL;
    readonly OR_LABEL = TRANSLATION_OR_LABEL;
    readonly RETRY_BTN = TRANSLATION_RETRY_BTN;
    readonly CANCEL_BTN = TRANSLATION_CANCEL_BTN;

    constructor(private filesFacadeService: FilesFacadeService) {
        super();
    }

    onAddFiles(event: NgxDropzoneChangeEvent) {
        if (event.rejectedFiles.length) {
            this.rejectedFiles = true;
            return;
        }
        const fileUploadPackage = {
            id: new RandomStringPipe().transform(),
            file: event.addedFiles[0],
        };

        const headers = this.customHeaders?.reduce(
            (acc, curr) => {
                if (curr.includes(':')) {
                    const [name, value] = curr.split(':');
                    acc[name.trim()] = value;
                }
                return acc;
            },
            {} as Record<string, any>,
        );
        this.filesFacadeService.loadFileIndex(
            fileUploadPackage,
            this.customEndpoint as string,
            headers,
        );

        this.filesFacadeService
            .getFileUploadFileIndex$(fileUploadPackage)
            .pipe(
                filter((fileIndex) => Boolean(fileIndex)),
                take(1),
            )
            .subscribe((fileIndex) => this.fileUploaded.emit(fileIndex));

        this.fileUploadStatus$ = this.filesFacadeService
            .getFileUploadStatus$(fileUploadPackage)
            .pipe(
                takeUntil(this.destroyed$),
                tap((fileUploadStatus) => {
                    this.progressType$.next(
                        this.getProgressType(fileUploadStatus),
                    );
                }),
            );

        this.fileUploadProgress$ = this.filesFacadeService
            .getFileUploadProgress$(fileUploadPackage)
            .pipe(takeUntil(this.destroyed$));

        this.fileUploadPackage$.next(fileUploadPackage);
    }

    getProgressType(fileUploadStatus: FileUploadStatus) {
        if (fileUploadStatus) {
            return {
                [FileUploadStatus.Ready]: 'success',
                [FileUploadStatus.Requested]: 'success',
                [FileUploadStatus.Started]: 'success',
                [FileUploadStatus.Failed]: 'danger',
                [FileUploadStatus.Completed]: 'success',
            }[fileUploadStatus];
        }
        return null;
    }

    onCancel() {
        this.resetState();
    }

    resetState() {
        this.fileUploadPackage$.next(null);
        this.rejectedFiles = false;
    }
}
